/**
 *
 * @module contentManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/content.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import ContentQuery from './contentQuery';
import BookmarkExtensions from './bookmarks/bookmarkExtensions';
import SearchExtensions from './search/searchExtensions';
import WatchlistExtensions from './watchlist/watchlistExtensions';
import SearchOverrides from './searchOverrides';

const ContentManagerConfiguration = Services.Configuration.ContentManagerConfiguration;
const ContentClient = Services.Content.ContentClient;
const AccessTokenProvider = Token.AccessTokenProvider;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    integer,
    positive,
    instanceStrict,
    nonEmptyString,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class ContentManager
 * @desc Provides a manager that can be used to access content data.
 *
 */
export default class ContentManager {

    /**
     *
     * @param {SDK.Services.Configuration.ContentManagerConfiguration} contentManagerConfiguration
     * @param {SDK.Services.Content.ContentClient} contentClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     *
     */
    constructor(contentManagerConfiguration, contentClient, logger = Logging, accessTokenProvider) {

        const errorMessage = {
            ctor: 'ContentManager(contentManagerConfiguration, contentClient, logger, accessTokenProvider)',
            get contentManagerConfiguration() {
                return `${this.ctor} needs ContentManagerConfiguration instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs AccessTokenProvider instance`;
            },
            get contentClient() {
                return `${this.ctor} needs ContentClient instance`;
            }
        };

        instanceStrict(contentManagerConfiguration, ContentManagerConfiguration, errorMessage.contentManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line
        instanceStrict(contentClient, ContentClient, errorMessage.contentClient, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.ContentManagerConfiguration}
         *
         */
        this.config = contentManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        /**
         *
         * @access private
         * @type {SDK.Services.Content.ContentClient}
         *
         */
        this.client = contentClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @desc Add extension methods
         *
         */
        BookmarkExtensions(ContentManager);
        SearchExtensions(ContentManager);
        WatchlistExtensions(ContentManager);

        this.logger.log(ContentManager.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {ContentQuery} options.query Query to be used to retrieve content.
     * @param {SDK.Content.SearchOverrides} [options.overrides] - Object which contains override information on the time
     * of origin and or the country of origin to apply to the query request
     * @param {String} [options.contentTransactionId] - An application developer-provided value to be included with the
     * HTTP request as the value of the `X-Content-Transaction-ID` header.
     * @desc Queries the a content service based on query context.
     * @returns {Promise<Object>} The GraphQL JSON returned from the content service.
     *
     */
    async query(options) {

        const errorMessage = {
            method: 'ContentManager.query(options)',
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get query() {
                return `${this.method} options.query should be an instance of SDK.Content.ContentQuery`;
            },
            get overrides() {
                return `${this.method} options.overrides should be an instance of SDK.Content.SearchOverrides`;
            },
            get contentTransactionId() {
                return `${this.method} options.contentTransactionId should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { query, overrides, contentTransactionId } = options;

        instanceStrict(query, ContentQuery, errorMessage.query, InvalidArgumentException);

        if (Check.assigned(overrides)) {
            instanceStrict(overrides, SearchOverrides, errorMessage.overrides, InvalidArgumentException);
        }

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const { logger, client } = this;

        logger.info(ContentManager.name, 'Getting content.');

        const queryOptions = {
            context: query.context,
            graphQlBuilder: query.graphQl,
            accessToken: this.accessToken,
            overrides,
            contentTransactionId
        };

        return await client.query(queryOptions);

    }

    /**
     *
     * @access public
     * @param {String} suggestionSet
     * @param {String} query
     * @param {Integer} [limit=null] - limit the number of results
     * @param {String} [filter=null] - filter results
     * @desc Gets search suggestions.
     * @returns {Promise<Array>} A list of search suggestions.
     *
     */
    async getSearchSuggestions(suggestionSet, query, limit = null, filter = null) {

        const errorMessage = {
            method: 'ContentManager.getSearchSuggestions(suggestionSet, query, limit, filter)',
            get suggestionSet() {
                return `${this.method} suggestionSet should be a String with length`;
            },
            get query() {
                return `${this.method} query should be a string with length`;
            },
            get limit() {
                return `${this.method} limit should be a positive integer`;
            },
            get filter() {
                return `${this.method} filter should be a string with length`;
            }
        };

        nonEmptyString(suggestionSet, errorMessage.suggestionSet, InvalidArgumentException);
        nonEmptyString(query, errorMessage.query, InvalidArgumentException);

        if (Check.assigned(limit)) {
            integer(limit, errorMessage.limit, InvalidArgumentException);
            positive(limit, errorMessage.limit, InvalidArgumentException);
        }

        if (Check.assigned(filter)) {
            nonEmptyString(filter, errorMessage.filter, InvalidArgumentException);
        }

        return await this.client.getSearchSuggestions(suggestionSet, query, limit, filter);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.accessTokenProvider.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/content/contentManager.js